import axios from 'axios';

/**
 * Service inbox API client.
 *
 * One file for the whole service, the way jobAuditApi.js is one file for
 * job-audit. The service inbox owns the emails, the review marks, rules.yaml's
 * category vocabulary and the task-routing table; this frontend serves its pages
 * but holds none of that data.
 *
 * Relative paths only: nginx proxies /api to the backends from the same origin
 * in production, and CRA's devServer proxy does the same locally, so the HttpOnly
 * auth cookie is sent automatically and there is no baseURL to configure.
 * `src/setupProxy.js` splits /api/inbox off to the inbox's port in dev.
 *
 * Two feature areas, two base paths — kept separate on purpose: the review
 * router's `/api/inbox/review/{email_id}` would swallow the routing sub-paths.
 */

// ---------------------------------------------------------------------------
// Classification review
// ---------------------------------------------------------------------------

// Served by the service inbox, not this backend: it owns the emails, the marks
// and rules.yaml's category list. This backend's /api/review router and its
// three tables were removed once both services shared a database. Nothing here
// is a copy of what they own; the page holds no email, no mark and no category
// vocabulary of its own.
const REVIEW_BASE = '/api/inbox/review';

/**
 * One page of the queue, newest arrival first.
 *
 * `reviewed` picks the half — false is the working queue, true is what has been
 * ruled on already (marks append, so a verdict can be revised), null is both.
 * `total` counts the filter rather than the page, which is what the pager needs.
 *
 * `reveal` asks for blind rows' predictions anyway — for reading the corpus
 * rather than labelling it. Sent only when true, so the default request is byte
 * for byte the one that was made before the flag existed.
 *
 * `category` narrows to one bucket, and `total` narrows with it. **This has to
 * be the server's filter, not a client one.** The page holds at most `limit`
 * rows, so filtering here could only ever hide rows already on screen — a
 * different act with the same appearance, and one the pager would contradict.
 * `BLIND_BUCKET` is a valid value; an unknown category is an empty page rather
 * than an error, so a stale selection shows nothing rather than throwing.
 *
 * `conversationId` narrows to one Graph thread, and is a server filter for the
 * same reason `category` is, only more so: a thread's messages need not share a
 * page, so grouping the loaded rows would show two of three and call it the
 * thread. An unknown id is an empty page, like an unknown category.
 */
export const listQueue = async ({
  reviewed = null,
  limit = 50,
  offset = 0,
  reveal = false,
  category = null,
  conversationId = null,
  needsRemark = false,
  blindDisagreed = false,
} = {}) => {
  const params = { limit, offset };
  if (reviewed !== null) params.reviewed = reviewed;
  if (reveal) params.reveal = true;
  if (category) params.category = category;
  if (conversationId) params.conversation_id = conversationId;
  // Only sent when on. The server defaults it false, and an explicit
  // `needs_remark=false` on every request would put a filter nobody chose into
  // the query string of every page.
  if (needsRemark) params.needs_remark = true;
  // The other backlog, and disjoint from the one above: that filter selects
  // marks whose reviewer was shown the prediction, this one marks whose
  // reviewer was not. Sending both would always be an empty page, which is why
  // the toggles turn each other off rather than combining.
  if (blindDisagreed) params.blind_disagreed = true;
  const { data } = await axios.get(`${REVIEW_BASE}/queue`, { params });
  return data;
};

/**
 * The blind slice's bucket name, matching `evaluation/blind.py`'s BLIND_BUCKET.
 *
 * A withheld row cannot be counted under a category, because its category *is*
 * the prediction being withheld. It goes here instead, on both sides.
 */
export const BLIND_BUCKET = 'blind';

/**
 * How the queue divides by predicted category, over the whole filter.
 *
 * Separate from `listQueue` so that paging does not recompute a figure that
 * does not change as you page. It does change with `reviewed`, with `reveal`
 * (which empties the blind bucket into the real categories), and when a mark
 * moves a row out of the unreviewed half — so the caller refetches on all three.
 *
 * Counting these client-side is what this replaced. Off one page they reported
 * the page's composition while looking like the queue's: "Billing 1" on a queue
 * holding forty of them, renumbering as the reviewer walked the pager.
 */
export const getCounts = async ({ reviewed = null, reveal = false } = {}) => {
  const params = {};
  if (reviewed !== null) params.reviewed = reviewed;
  if (reveal) params.reveal = true;
  const { data } = await axios.get(`${REVIEW_BASE}/meta/counts`, { params });
  return data;
};

/**
 * One email in full: body, signals and every mark on it.
 *
 * Separate from the queue on purpose — the list carries no bodies, so opening a
 * row is one small request rather than every page being a payload of full
 * customer emails.
 *
 * Blind rows come back with `prediction: null` and their marks'
 * `predicted_category_at_review` nulled. Both are enforced server-side; do not
 * try to reconstruct either here — `reveal` is the only way to get them, and it
 * is answered by the server marking the row `revealed` so the consequence
 * travels with the data.
 */
export const getDetail = async (id, { reveal = false } = {}) => {
  const { data } = await axios.get(
    `${REVIEW_BASE}/${id}`,
    reveal ? { params: { reveal: true } } : {}
  );
  return data;
};

/**
 * Append a verdict.
 *
 * `mark.revealed` must say whether the prediction was on screen. The server
 * records `shown_prediction = (not blind) or revealed`, so getting this wrong in
 * the false direction files a verdict read off the screen as an independent
 * label and corrupts the blind-accuracy number — the one thing the blind slice
 * exists to produce. Callers pass the row's own `revealed`, never the page's
 * current toggle, because a row opened before the toggle flipped was judged
 * under whatever was true then.
 */
export const saveMark = async (id, mark) => {
  const { data } = await axios.post(`${REVIEW_BASE}/${id}/mark`, mark);
  return data;
};

/** Progress counters plus the blind-vs-prefilled numbers the design exists for. */
export const getStats = async () => {
  const { data } = await axios.get(`${REVIEW_BASE}/meta/stats`);
  return data;
};

/**
 * The category vocabulary, from rules.yaml on the serving side.
 *
 * Fetched rather than duplicated here. The mark endpoint rejects a category it
 * does not know, and on a blind row choosing one is the entire task, so the
 * list has to be the server's own.
 */
// The folders the service inbox files mail to, from rules.yaml, for the
// Mailbox's "Service inbox folders only".
export const listFilingFolders = async () => {
  const { data } = await axios.get(`${REVIEW_BASE}/meta/folders`);
  return data;
};

export const listCategories = async () => {
  const { data } = await axios.get(`${REVIEW_BASE}/meta/categories`);
  return data;
};

// `GET ${REVIEW_BASE}/meta/labels.csv` exports the review marks as a labeled test
// set, in the shape the service inbox's `read_labels` consumes. Deliberately not
// wired up here: it is a developer's artefact, and this page is for verifying
// classifications rather than scoring the classifier. The everyday path no
// longer needs the file at all — `evaluate --from-db` reads the same marks
// straight from the store — so the file is for pinning a set that two runs can
// be compared across.

export const SIGNAL_FIELDS = ['name', 'phone', 'email', 'zip', 'address'];

/**
 * Per-signal verdicts, matching SignalVerdict on the serving side.
 *
 * `na` and `unsure` are different answers: `na` means the email carried no such
 * signal, so there was nothing to get right, while `unsure` means it did and the
 * reviewer could not tell. A field left unticked was not judged at all.
 */
export const SIGNAL_VERDICTS = ['correct', 'incorrect', 'unsure', 'na'];

// ---------------------------------------------------------------------------
// Task routing
// ---------------------------------------------------------------------------

// Sets who ServiceTitan tasks go to per tenant and category (`assignedToId`) and
// who each tenant's tasks are reported by (`reportedById`). Stored in the inbox's
// `service_inbox` schema and keyed on the same category vocabulary the review
// page uses, which is why it lives here and not in this platform's schemas.
const ROUTING_BASE = '/api/inbox/routing';

/**
 * The whole routing table in one call: the eight residential (RSR) tenants, the
 * full category list, every stored route and reporter, each category's own
 * forward list (`category_forwards`), and `can_edit` (the session's admin claim).
 * RNC and FSO are never listed and a write for one is refused server-side.
 */
export const getRoutingOverview = async () => {
  const { data } = await axios.get(ROUTING_BASE);
  return data;
};

/**
 * A route carries two settings for the category: the ServiceTitan task assignee
 * and who the email is forwarded to. They can be different people.
 *
 * `route` is `{ employee_id: int|null, name: str|null, email: str|null,
 * forward_to: string[] }`. The server requires either an assignee (employee_id
 * or email) or at least one forward_to address — a route may be assign-only,
 * forward-only, or both. Admin only (403 otherwise). Returns the stored entry.
 */
export const putRoute = async (tenantId, category, route) => {
  const { data } = await axios.put(
    `${ROUTING_BASE}/routes/${encodeURIComponent(tenantId)}/${encodeURIComponent(category)}`,
    route
  );
  return data;
};

/** 204 on success, 404 if there was no entry to remove. Admin only. */
export const deleteRoute = async (tenantId, category) => {
  await axios.delete(
    `${ROUTING_BASE}/routes/${encodeURIComponent(tenantId)}/${encodeURIComponent(category)}`
  );
};

/**
 * A category's own forward list, the first level of forwarding (2026-10-19):
 * what a tenant route with an empty `forward_to` inherits, and all an email
 * with no matched customer has. At least one address; admin only.
 */
export const putCategoryForward = async (category, forwardTo) => {
  const { data } = await axios.put(
    `${ROUTING_BASE}/categories/${encodeURIComponent(category)}`,
    { forward_to: forwardTo }
  );
  return data;
};

/** 204 on success, 404 if the category had no list. Admin only. */
export const deleteCategoryForward = async (category) => {
  await axios.delete(`${ROUTING_BASE}/categories/${encodeURIComponent(category)}`);
};

/** One reporter per tenant (no category), same `person` shape. Admin only. */
export const putReporter = async (tenantId, person) => {
  const { data } = await axios.put(
    `${ROUTING_BASE}/reporters/${encodeURIComponent(tenantId)}`,
    person
  );
  return data;
};

/** 204 on success, 404 if there was no reporter to remove. Admin only. */
export const deleteReporter = async (tenantId) => {
  await axios.delete(`${ROUTING_BASE}/reporters/${encodeURIComponent(tenantId)}`);
};

// ---------------------------------------------------------------------------
// Mailbox viewer
// ---------------------------------------------------------------------------

// The live service mailbox, read from Microsoft Graph by the service inbox, not
// from its database. So it shows where mail really is, including anything a
// person filed by hand. Read-only, and part of the service_inbox feature like
// the review routes; nothing here can move mail.
const MAILBOX_BASE = '/api/inbox/mailbox';

/** `{ mailbox, folders }`: the top level of the folder tree, with counts. */
export const getMailboxFolders = async () => {
  const { data } = await axios.get(`${MAILBOX_BASE}/folders`);
  return data;
};

/** One folder's subfolders. The tree loads a level at a time. */
export const getFolderChildren = async (folderId) => {
  const { data } = await axios.get(
    `${MAILBOX_BASE}/folders/${encodeURIComponent(folderId)}/children`
  );
  return data;
};

/**
 * One page of a folder, newest first, without bodies. There is no total here;
 * the folder's own `total` from the tree is what the pager uses.
 *
 * `filters` narrows it: `{ q, unread, attachments, category, task }`, where
 * `category` / `task` may be a string or an array. `category` takes a category
 * key or 'unprocessed'; `task` a task status or 'none'. A filtered page comes
 * back with `filtered: true`, and its `offset` / `next_offset` are positions in
 * the unfiltered folder: pass `next_offset` as the next page's `offset`. It is
 * null once the whole folder has been searched.
 */
export const getFolderMessages = async (
  folderId,
  { limit = 50, offset = 0, filters = {} } = {}
) => {
  // Repeated keys (category=a&category=b), which the service reads as a list.
  // axios's default would send category[]=a, which it does not.
  const params = new URLSearchParams({ limit, offset });
  const { q, unread, attachments, category, task, since, until, wouldBook } = filters;
  if (q && q.trim()) params.append('q', q.trim());
  // A received window (Dates), sent on to Graph as a receivedDateTime filter.
  if (since) params.append('since', since.toISOString());
  if (until) params.append('until', until.toISOString());
  if (unread != null) params.append('unread', unread);
  if (attachments != null) params.append('attachments', attachments);
  [].concat(category || []).forEach((c) => params.append('category', c));
  [].concat(task || []).forEach((t) => params.append('task', t));
  // Only the emails the (switched off) automatic booking rules would book.
  if (wouldBook != null) params.append('would_book', wouldBook);
  const { data } = await axios.get(
    `${MAILBOX_BASE}/folders/${encodeURIComponent(folderId)}/messages`,
    { params }
  );
  return data;
};

/**
 * The messages of one conversation the service inbox has processed, oldest
 * first, from its database (not a mailbox search): summaries with `stored`.
 * Each `id` may be stale after a move, so open one by `internet_message_id`
 * (`locateMessage`). Replies sent from the mailbox are not included.
 */
export const getConversation = async (conversationId) => {
  const { data } = await axios.get(`${MAILBOX_BASE}/conversation`, {
    params: { conversation_id: conversationId },
  });
  return data;
};

/** One message in full, body included. Opening it does not mark it read. */
export const getMailboxMessage = async (messageId) => {
  const { data } = await axios.get(
    `${MAILBOX_BASE}/messages/${encodeURIComponent(messageId)}`
  );
  return data;
};

// ---------------------------------------------------------------------------
// Hand-made ServiceTitan tasks
// ---------------------------------------------------------------------------

/**
 * Mark the customer an email belongs to, by hand. `customer` is the search
 * result or lookup candidate the person picked: `{ tenant_id, customer_id,
 * name, office, addresses, is_rnc }`. Replaces any earlier mark and resolves to
 * the stored mark (`confirmed_by`, `confirmed_at`). Never touches the lookup's
 * own result.
 */
export const confirmCustomer = async (emailId, customer) => {
  const { data } = await axios.put(`/api/inbox/confirmations/${encodeURIComponent(emailId)}`, {
    tenant_id: customer.tenant_id,
    customer_id: customer.customer_id,
    name: customer.name || null,
    office: customer.office || null,
    location: (customer.addresses || [])[0] || null,
    is_rnc: !!customer.is_rnc,
  });
  return data;
};

/** Clear a hand-marked match; the email shows the lookup's own result again. */
export const clearConfirmation = async (emailId) => {
  await axios.delete(`/api/inbox/confirmations/${encodeURIComponent(emailId)}`);
};

/**
 * Create a ServiceTitan task for one stored email, against a picked customer.
 *
 * `{ email_id, tenant_id, customer_id, category, summary }`, plus
 * `assigned_to_employee_id` / `reported_by_employee_id` only where Task Routing
 * has no employee for that role (the service refuses one that contradicts a
 * route). Resolves to the recorded task outcome; `status: 'error'` means the
 * platform was called and no task id came back. A 409 means the email already
 * has a task or one is in flight. A ServiceTitan task cannot be deleted.
 */
export const createTask = async (body) => {
  const { data } = await axios.post('/api/inbox/tasks', body);
  return data;
};

/**
 * Create a ServiceTitan booking for one email's matched customer (service
 * inbox): `{ email_id, tenant_id, customer_id, summary, start, priority }`.
 * `start` is an ISO time with its offset, or null to leave it to the CSR who
 * schedules the booking. Resolves to the recorded outcome; `status: 'error'`
 * means the platform was called and no booking id came back. A 409 means the
 * email already has a booking or one is in flight.
 */
export const createBooking = async (body) => {
  const { data } = await axios.post('/api/inbox/bookings', body);
  return data;
};

/**
 * The open ServiceTitan appointments of one email's matched customer, from the
 * platform's synced tables: `{ appointments, truncated }`. Each has `start`,
 * `status`, `job_type`, `job_number`, `servicetitan_url` (its job's page, null
 * where no link can be built) and `ours` (its job came from a booking made
 * here). Empty for an email with no matched customer, or an RNC one.
 */
export const getCustomerAppointments = async (emailId) => {
  const { data } = await axios.get('/api/inbox/appointments', { params: { email_id: emailId } });
  return data;
};

/**
 * How many open ServiceTitan appointments the matched customer of each listed
 * email has, for the Mailbox's list rows: `{ counts: { <email id>: n } }`. An
 * email whose customer has none, or that has no customer, is left out. Up to
 * 200 ids a call.
 */
export const getAppointmentCounts = async (emailIds) => {
  const { data } = await axios.post('/api/inbox/appointments/counts', { email_ids: emailIds });
  return data;
};

/**
 * One ServiceTitan booking as ServiceTitan holds it now (platform, live):
 * `{ booking, fetched_at }`, `booking` the API's own JSON (status New,
 * Accepted, Converted or Dismissed; `jobId` once converted).
 */
export const getBookingLive = async (tenantId, bookingId) => {
  const { data } = await axios.get(
    `/api/servicetitan/bookings/${encodeURIComponent(bookingId)}`,
    { params: { tenant_id: tenantId } }
  );
  return data;
};

/**
 * Change a booking's `start`, `priority` or `summary` (platform). Only a
 * booking made from the service inbox. Resolves to the booking as it now is.
 */
export const updateBooking = async (tenantId, bookingId, changes) => {
  const { data } = await axios.patch(
    `/api/servicetitan/bookings/${encodeURIComponent(bookingId)}`,
    changes,
    { params: { tenant_id: tenantId } }
  );
  return data;
};

/** Dismiss a booking made from the service inbox (platform), with an optional memo. */
export const dismissBooking = async (tenantId, bookingId, memo) => {
  const { data } = await axios.post(
    `/api/servicetitan/bookings/${encodeURIComponent(bookingId)}/dismiss`,
    { memo: memo || null },
    { params: { tenant_id: tenantId } }
  );
  return data;
};

/**
 * Search ServiceTitan customers across every tenant: name and street words,
 * a phone number, an email, or a customer id. Served by the platform, not the
 * service inbox. One row per customer and address, RNC labelled not filtered.
 */
export const searchCustomers = async (q, { limit = 25 } = {}) => {
  const { data } = await axios.get('/api/customer-search', { params: { q, limit } });
  return data;
};

/**
 * One ServiceTitan customer as ServiceTitan holds it now (platform, live):
 * `{ customer, customer_contacts, locations, location_contacts, errors,
 * fetched_at }`, each part the API's own JSON. A part that could not be read is
 * named in `errors` and the rest still comes back.
 */
export const getCustomerLive = async (tenantId, customerId) => {
  const { data } = await axios.get(
    `/api/servicetitan/customers/${encodeURIComponent(customerId)}`,
    { params: { tenant_id: tenantId } }
  );
  return data;
};

/**
 * One ServiceTitan task as ServiceTitan holds it now (platform, live on every
 * call, never synced or cached): `{ task, employees, labels, errors,
 * fetched_at }`. `task` is the API's own JSON with subtasks; `employees` and
 * `labels` name the ids in it. ServiceTitan offers no way to edit a task.
 */
export const getTaskLive = async (tenantId, taskId) => {
  const { data } = await axios.get(`/api/servicetitan/tasks/${encodeURIComponent(taskId)}`, {
    params: { tenant_id: tenantId },
  });
  return data;
};

/**
 * Where up to 50 tasks in one tenant stand now (platform, one live ServiceTitan
 * call): `{ tasks: [{ task_id, status, is_closed, complete_by, closed_on }],
 * missing, fetched_at }`. Status fields only.
 */
export const getTaskStatuses = async (tenantId, taskIds) => {
  const { data } = await axios.get('/api/servicetitan/tasks', {
    params: { tenant_id: tenantId, ids: taskIds.join(',') },
  });
  return data;
};

/**
 * The Ops Dashboard's figures (the SOW's ops digest) for mail received in [since, until): volume by category,
 * handled automatically versus flagged, confidence, lookups, tasks and
 * forwards. `since` / `until` are Dates; the reader's own day or week.
 */
export const getDigest = async (since, until) => {
  const { data } = await axios.get('/api/inbox/digest', {
    params: { since: since.toISOString(), until: until.toISOString() },
  });
  return data;
};

/**
 * The emails behind one category's count in the digest, newest first, from the
 * store: `{ category, total, emails: [...], truncated }`. `category` is a key,
 * or "unclassified".
 */
export const getDigestEmails = async (since, until, category) => {
  const { data } = await axios.get('/api/inbox/digest/emails', {
    params: { since: since.toISOString(), until: until.toISOString(), category },
  });
  return data;
};

/** Where a message is now, found by its Message-ID: `{ id, folder_id }`, or a 404. */
export const locateMessage = async (internetMessageId) => {
  const { data } = await axios.get(`${MAILBOX_BASE}/locate`, {
    params: { internet_message_id: internetMessageId },
  });
  return data;
};

/** Everything the service inbox stored for one email: classification, prompts, model calls, lookup, task. */
export const getEmailRecord = async (emailId) => {
  const { data } = await axios.get(`/api/inbox/emails/${encodeURIComponent(emailId)}/record`);
  return data;
};

/**
 * What a task form can choose from in one tenant (platform, live from
 * ServiceTitan, cached an hour): `task_types`, `business_units` and
 * `task_sources` as `[{ id, name }]`, `priorities`, and `defaults` (each
 * category's task type by name, the business unit and source the automatic
 * writer uses).
 */
export const getTaskReference = async (tenantId) => {
  const { data } = await axios.get('/api/servicetitan/task-reference', {
    params: { tenant_id: tenantId },
  });
  return data;
};

/** Active ServiceTitan employees in one tenant (platform, silver.st_employees). */
export const listEmployees = async (tenantId) => {
  const { data } = await axios.get('/api/servicetitan/employees', {
    params: { tenant_id: tenantId },
  });
  return data || [];
};

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/**
 * The server's own message for a rejected request.
 *
 * Worth surfacing verbatim: a 422 from the mark endpoint names the valid
 * categories, and a 422 from a routing write names why the tenant, category or
 * contact was refused — both more useful than anything this page could word.
 */
export const apiMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};
